import React, { useEffect, useState } from 'react';
import { fetchTarifas } from '../infrastructure/productApi';
import { useComercialContext } from '../../comercial/ComercialContext';
import { FormattedNumber } from '../../common/ui/FormattedNumber';

/**
 * Tarifas del artículo para comerciales con el permiso "Ver tarifas" (admin): una fila por grupo de
 * precio (o grupo de descuento / general), con precio, descuentos, cantidad mínima y vigencia. La
 * tarifa del cliente suplantado sale resaltada y primera; si tiene precio de excepción, aparte.
 * Solo lectura: el precio del pedido lo sigue calculando el servidor.
 */
const descuentos = (t) => {
  const partes = [];
  if (t.descuento) partes.push(`dto ${t.descuento}%`);
  ['desc1', 'desc2', 'desc3', 'desc4', 'desc5', 'desc6', 'desc7', 'desc8'].forEach((k, i) => {
    if (t[k]) partes.push(`d${i + 1} ${t[k]}%`);
  });
  return partes.join(' · ');
};

const vigencia = (t) => {
  if (!t.fechaInicio && !t.fechaFin) return '—';
  return `${t.fechaInicio || '…'} → ${t.fechaFin || 'sin fin'}`;
};

export const TarifasComercial = ({ productCode }) => {
  const { actingAsCliente } = useComercialContext();
  const numCliente = actingAsCliente?.numCliente ?? null;
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setDatos(null);
    setError(null);
    fetchTarifas(productCode, numCliente)
      .then(r => { if (vivo) setDatos(r); })
      .catch(() => { if (vivo) setError('No se han podido cargar las tarifas'); });
    return () => { vivo = false; };
  }, [abierto, productCode, numCliente]);

  return (
    <div className="mt-3" data-testid="tarifas-comercial">
      <button type="button" className="btn btn-link p-0 fw-bold" onClick={() => setAbierto(a => !a)}>
        {abierto ? '▾' : '▸'} Tarifas del artículo <span className="text-muted fw-normal small">(solo visible para comerciales)</span>
      </button>
      {abierto && (
        <div className="mt-2">
          {error && <div className="text-danger small">{error}</div>}
          {!error && !datos && <div className="text-muted small">Cargando tarifas…</div>}
          {datos && (
            <>
              {datos.grupoPrecioCliente && (
                <p className="small mb-1">
                  Grupo de precio de {actingAsCliente?.nombreRazonSocial ?? 'este cliente'}: <strong>{datos.grupoPrecioCliente}</strong>
                </p>
              )}
              {datos.excepciones?.length > 0 && (
                <div className="alert alert-info py-1 px-2 small mb-2">
                  <strong>Precio de excepción de este cliente:</strong>{' '}
                  {datos.excepciones.map((e, i) => (
                    <span key={i} className="me-2">
                      <FormattedNumber value={e.precio} config={{ style: 'currency' }} />
                      {e.cantidadMinima > 1 ? ` desde ${e.cantidadMinima} uds` : ''}
                      {descuentos(e) ? ` (${descuentos(e)})` : ''}
                      {e.lineaNegocio ? ` · línea ${e.lineaNegocio}` : ''}
                    </span>
                  ))}
                </div>
              )}
              {datos.tarifas.length === 0 ? (
                <div className="text-muted small">Este artículo no tiene tarifas.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm small mb-0">
                    <thead>
                      <tr>
                        <th>Tarifa</th>
                        <th className="text-end">Precio</th>
                        <th>Descuentos</th>
                        <th className="text-end">Cant. mín.</th>
                        <th>Vigencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.tarifas.map((t, i) => (
                        <tr key={i} className={t.esDelCliente ? 'table-warning fw-bold' : ''}
                            data-testid={t.esDelCliente ? 'tarifa-cliente' : undefined}>
                          <td>{t.grupoPrecio ?? (t.grupoDescuento ? `Dto. ${t.grupoDescuento}` : 'General')}{t.esDelCliente ? ' ★' : ''}</td>
                          <td className="text-end"><FormattedNumber value={t.precio} config={{ style: 'currency' }} /></td>
                          <td>{descuentos(t) || '—'}</td>
                          <td className="text-end">{t.cantidadMinima}</td>
                          <td>{vigencia(t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
